/** @jsxImportSource @emotion/react */
import React from "react";
import { Alert, Col, Form, Modal, Row, Button, Select } from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Styles from "./styles";
import { HomeUsecase, useHomeDispatch } from "../../../usecases/Home";
import { homeFormSchema } from "../ModalChangeStatusHome/formSchema";
import {
  UpdateHomeDto,
  HomeStatusArray,
  StatusOption,
} from "../../../services/Home/dto/update.dto";
import { GetHomeDto } from "../../../services/Home/dto/get.dto";

interface ModalChangeStatusHomeProps {
  onCancel: () => void;
  onSuccess: () => void;
  visible: boolean;
  item: GetHomeDto;
}

const ModalChangeStatusHomeComponent: React.FC<ModalChangeStatusHomeProps> = ({
  visible,
  onCancel,
  onSuccess,
  item,
}: ModalChangeStatusHomeProps) => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<UpdateHomeDto>({
    mode: "onSubmit",
    defaultValues: new UpdateHomeDto(item),
    resolver: yupResolver(homeFormSchema),
  });
  const homeDispatch = useHomeDispatch();

  const handleSubmitHandle = React.useCallback(
    async (data: UpdateHomeDto) => {
      setIsProcessing(true);
      try {
        if (homeDispatch) {
          const homeUsecase = new HomeUsecase(homeDispatch);
          await homeUsecase.update(data);
        }
        reset();
        setErrorMessage("");
        onSuccess();
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
      } finally {
        setIsProcessing(false);
      }
    },
    [homeDispatch, reset, onSuccess],
  );

  const onCancelHandle = React.useCallback(() => {
    reset();
    setErrorMessage("");
    onCancel();
  }, [onCancel, reset]);

  return (
    <Modal
      title="Change Status"
      visible={visible}
      closable={false}
      footer={[
        <Button key="back" size="large" onClick={onCancelHandle}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          loading={isProcessing}
          onClick={handleSubmit(handleSubmitHandle)}
        >
          Save
        </Button>,
      ]}
    >
      <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
        {errorMessage && (
          <Row justify="center">
            <Col css={Styles.colGutterStyle}>
              <Alert message={errorMessage} type="error" />
            </Col>
          </Row>
        )}
        <Form.Item
          label="Status"
          css={Styles.labelStyle}
          validateStatus={errors.status ? "error" : "validating"}
          help={errors.status?.message}
        >
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select {...field} size="large">
                {HomeStatusArray.filter((option) =>
                  StatusOption[`${item.status}`].includes(option),
                ).map((key) => (
                  <Select.Option key={key} value={key}>
                    {key}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const ModalChangeStatusHome = React.memo(ModalChangeStatusHomeComponent);
